import { format, parseISO } from 'date-fns';
import type { SentinelAnalyticsSeriesPoint } from '@/types/sentinelAnalytics';

/** Daily values for trend lines; null when the day has nothing to divide by. */
function percent(count: number, total: number): number | null {
  return total === 0 ? null : (count / total) * 100;
}

function minutes(milliseconds: number | null): number | null {
  return milliseconds === null ? null : milliseconds / 60_000;
}

export const SERIES_VALUE = {
  inScopeTickets: (p: SentinelAnalyticsSeriesPoint) => p.inScopeTickets,
  totalTickets: (p: SentinelAnalyticsSeriesPoint) => p.totalTickets,
  inScopeShare: (p: SentinelAnalyticsSeriesPoint) => percent(p.inScopeTickets, p.totalTickets),
  outOfScopeShare: (p: SentinelAnalyticsSeriesPoint) => percent(p.outOfScopeTickets, p.totalTickets),
  unassignment: (p: SentinelAnalyticsSeriesPoint) => percent(p.unassignedTickets, p.inScopeTickets),
  missedOpportunity: (p: SentinelAnalyticsSeriesPoint) => percent(p.missedOpportunityTickets, p.totalTickets),
  handled: (p: SentinelAnalyticsSeriesPoint) => percent(p.handledTickets, p.inScopeTickets),
  automation: (p: SentinelAnalyticsSeriesPoint) => percent(p.automatedTickets, p.inScopeTickets),
  reopen: (p: SentinelAnalyticsSeriesPoint) => percent(p.reopenedTickets, p.resolvedTickets),
  ftnr: (p: SentinelAnalyticsSeriesPoint) => percent(p.regrettableReopenedTickets, p.resolvedTickets),
  aiCoverage: (p: SentinelAnalyticsSeriesPoint) => percent(p.coveredTurns, p.turns),
  aiGenerationFailure: (p: SentinelAnalyticsSeriesPoint) => percent(p.failedTurns, p.turns),
  aiAccuracy: (p: SentinelAnalyticsSeriesPoint) => percent(p.accurateDrafts, p.gradedDrafts),
  correctedResponses: (p: SentinelAnalyticsSeriesPoint) => percent(p.correctedDrafts, p.gradedDrafts),
  firstResponseMinutes: (p: SentinelAnalyticsSeriesPoint) => minutes(p.firstResponseAverageMs),
  resolutionMinutes: (p: SentinelAnalyticsSeriesPoint) => minutes(p.resolutionAverageMs),
  firstResponseBusinessMinutes: (p: SentinelAnalyticsSeriesPoint) => minutes(p.firstResponseBusinessAverageMs),
  resolutionBusinessMinutes: (p: SentinelAnalyticsSeriesPoint) => minutes(p.resolutionBusinessAverageMs),
} as const;

export type SeriesValue = (point: SentinelAnalyticsSeriesPoint) => number | null;

export interface TrendRow {
  label: string;
  value: number | null;
  background: number | null;
  /** The series' only day with data: there is no line to draw, so the chart draws it as a dot. */
  lone: boolean;
}

export function trendRows(series: SentinelAnalyticsSeriesPoint[], value: SeriesValue, background?: SeriesValue): TrendRow[] {
  // Days after today have no data yet, so the x-axis ends at today.
  const today = format(new Date(), 'yyyy-MM-dd');
  const upToToday = series.filter((point) => point.date <= today);
  const withData = upToToday.flatMap((point, index) => (value(point) === null ? [] : [index]));
  const single = withData.length === 1;
  // The line spans its first to last day with data, so it never floats mid-axis with walls at both ends.
  const days = withData.length <= 1 ? upToToday : upToToday.slice(withData[0], (withData.at(-1) ?? -1) + 1);
  const values = days.map(value);
  return days.map((point, index) => ({
    label: format(parseISO(point.date), 'MMM d'),
    value: values[index],
    background: background ? background(point) : null,
    lone: single && values[index] !== null,
  }));
}
